import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useEffect, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { useLanguageSync } from './hooks/useLanguageSync';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { initializeSocket, disconnectSocket, setNotificationCallback } from './services/socket';
import { setAxiosNotificationCallback } from './api/axios';
import { RootState } from './store';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import LanguageRouteSync from './components/LanguageRouteSync';
import MainLayout from './components/layout/MainLayout';
import { CircularProgress, Box } from '@mui/material';
import i18n from './i18n';

// Lazy load page components for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const OtpVerificationPage = lazy(() => import('./pages/OtpVerificationPage'));
const WorkspaceSelectorPage = lazy(() => import('./pages/WorkspaceSelectorPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ContentLibraryPage = lazy(() => import('./pages/ContentLibraryPage'));
const PlaylistsPage = lazy(() => import('./pages/PlaylistsPage'));
const LayoutsPage = lazy(() => import('./pages/LayoutsPage'));
const LayoutEditorPage = lazy(() => import('./pages/LayoutEditorPage'));
const DisplaysPage = lazy(() => import('./pages/DisplaysPage'));
const DisplayDetailPage = lazy(() => import('./pages/DisplayDetailPage'));
const SchedulesPage = lazy(() => import('./pages/SchedulesPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const WorkspaceSettingsPage = lazy(() => import('./pages/WorkspaceSettingsPage'));
const WorkspaceManagementPage = lazy(() => import('./pages/WorkspaceManagementPage'));
const EmailLogsPage = lazy(() => import('./pages/EmailLogsPage'));
const ScreenSharingPage = lazy(() => import('./pages/ScreenSharingPage'));

/**
 * Loading fallback component for lazy-loaded routes
 */
function LoadingFallback() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <CircularProgress />
    </Box>
  );
}

/**
 * WebSocket and API error handler initializer component
 * Initializes WebSocket connection and sets up API error notifications
 * Requirements: 9.1, 9.6, All (error handling)
 */
function WebSocketInitializer() {
  const { showNotification, showError } = useNotification();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const currentWorkspace = useSelector((state: RootState) => state.auth.currentWorkspace);

  useEffect(() => {
    // Set notification callback for socket events
    setNotificationCallback(showNotification);
    
    // Set notification callback for axios errors
    setAxiosNotificationCallback(showError);

    // Initialize socket when authenticated and workspace is selected
    if (isAuthenticated && currentWorkspace) {
      console.log('Initializing WebSocket connection...');
      initializeSocket();
      
      return () => {
        console.log('Disconnecting WebSocket...');
        disconnectSocket();
      };
    }
    
    return undefined;
  }, [isAuthenticated, currentWorkspace, showNotification, showError]);

  return null;
}

function App() {
  // Sync language preference with backend
  useLanguageSync();

  // Get current language for redirects
  const currentLanguage = i18n.language || 'uz';

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <NotificationProvider>
            <AuthProvider>
              <WebSocketInitializer />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/:lang/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/:lang/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/:lang/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/:lang/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-otp" element={<OtpVerificationPage />} />
                  <Route path="/:lang/verify-otp" element={<OtpVerificationPage />} />

                  {/* Protected routes without layout */}
                  <Route
                    path="/workspace-selector"
                    element={
                      <ProtectedRoute>
                        <WorkspaceSelectorPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/:lang/workspace-selector"
                    element={
                      <ProtectedRoute>
                        <WorkspaceSelectorPage />
                      </ProtectedRoute>
                    }
                  />

              {/* Protected routes with main layout - with language prefix */}
              <Route
                path="/:lang/dashboard"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <DashboardPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/content"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <ContentLibraryPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/content/folder/:folderId"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <ContentLibraryPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/content/:contentId"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <ContentLibraryPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/playlists"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <PlaylistsPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/layouts"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <LayoutsPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/layouts/editor/:layoutId"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <LayoutEditorPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/displays"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <DisplaysPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/displays/:displayId"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <DisplayDetailPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/schedules"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <SchedulesPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/analytics"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <AnalyticsPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/users"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <UserManagementPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/emails"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <EmailLogsPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/screen-sharing"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <ScreenSharingPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/workspaces"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <WorkspaceManagementPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/workspace/:workspaceId/settings"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <WorkspaceSettingsPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:lang/settings"
                element={
                  <ProtectedRoute>
                    <LanguageRouteSync>
                      <MainLayout>
                        <WorkspaceSettingsPage />
                      </MainLayout>
                    </LanguageRouteSync>
                  </ProtectedRoute>
                }
              />

              {/* Legacy routes without language prefix - redirect to language version */}
              <Route path="/dashboard" element={<Navigate to={`/${currentLanguage}/dashboard`} replace />} />
              <Route path="/content" element={<Navigate to={`/${currentLanguage}/content`} replace />} />
              <Route path="/content/folder/:folderId" element={<Navigate to={`/${currentLanguage}/content/folder/:folderId`} replace />} />
              <Route path="/content/:contentId" element={<Navigate to={`/${currentLanguage}/content/:contentId`} replace />} />
              <Route path="/playlists" element={<Navigate to={`/${currentLanguage}/playlists`} replace />} />
              <Route path="/layouts" element={<Navigate to={`/${currentLanguage}/layouts`} replace />} />
              <Route path="/displays" element={<Navigate to={`/${currentLanguage}/displays`} replace />} />
              <Route path="/schedules" element={<Navigate to={`/${currentLanguage}/schedules`} replace />} />
              <Route path="/analytics" element={<Navigate to={`/${currentLanguage}/analytics`} replace />} />
              <Route path="/users" element={<Navigate to={`/${currentLanguage}/users`} replace />} />
              <Route path="/workspaces" element={<Navigate to={`/${currentLanguage}/workspaces`} replace />} />
              <Route path="/settings" element={<Navigate to={`/${currentLanguage}/settings`} replace />} />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </NotificationProvider>
    </BrowserRouter>
  </ThemeProvider>
  </ErrorBoundary>
  );
}

export default App;
